import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Subscribe to a single measurement result document:
 * measurements/{uid}/{variant}/{docId}
 *
 * - If the doc doesn't exist yet (e.g. backend processing), `exists=false` and `data=null`.
 * - Stays subscribed, so the UI updates as soon as it becomes available.
 */
export function useMeasurementResultDoc(db, uid, variant, docId) {
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(!!db && !!uid && !!variant && !!docId);
  const [error, setError] = useState(null);

  const ref = useMemo(() => {
    if (!db || !uid || !variant || !docId) return null;
    return doc(db, "measurements", uid, variant, docId);
  }, [db, uid, variant, docId]);

  useEffect(() => {
    if (!ref) {
      setData(null);
      setExists(false);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setExists(snap.exists());
        setData(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [ref]);

  return { data, exists, loading, error };
}
